import { useCallback, useMemo, ReactNode } from 'react';
import { shallowEqual } from 'react-redux';
import { useAppSelector } from '../../../../../hooks/hooks';
import PrimitiveAccordionElement from '../../../AccordionElement';
import { useResponsiveTableContext } from '../ResponsiveTableContext';
import { TResponsiveTableRow } from '../types';
import AccordionCell from './AccordionCell';
import makeAccordionElementButton from './AccordionElementButton';
import { IAccordionElementButton } from '../../../AccordionElementButton';

export interface IAccordionElement {
  row: TResponsiveTableRow;
  rowIndex: number;
}

const AccordionElement = ({ row, rowIndex }: IAccordionElement) => {
  const { name } = useResponsiveTableContext();
  const indexColumns = useAppSelector((global) => {
    const tableState = global.responsiveTableSlice[name];
    return tableState?.accordionIndexColumns;
  }, shallowEqual);
  const columns = useAppSelector((global) => {
    const tableState = global.responsiveTableSlice[name];
    return tableState?.columns;
  }, shallowEqual);
  const hideColumns = columns.length === 1 && columns[0].label?.trim() === '';

  const ButtonComponent = useMemo(
    () => makeAccordionElementButton({ row, rowIndex }),
    [row, rowIndex],
  );

  let titleChildren: ReactNode = null;

  const title = useMemo(() => {
    if (row.title) return row.title;
    if (Array.isArray(indexColumns)) {
      return indexColumns
        .map((current) => {
          const html = row.cells[current.index].rendererProps?.html;
          if (html) {
            titleChildren = (
              // eslint-disable-next-line react-hooks/exhaustive-deps
              <span
                dangerouslySetInnerHTML={{
                  __html: (html as string).replaceAll(
                    /<TOK(\d+)/g,
                    '&lt;TOK$1',
                  ),
                }}
              />
            );
            return (html as string).replace(/<[^>]*>/g, '');
          }
          return (
            row.cells[current.index]?.title ??
            (row.cells[current.index]?.children as string)
          );
        })
        .join(' | ');
    } else {
      return row.cells[0]?.title ?? (row.cells[0]?.children as string);
    }
  }, [indexColumns, row]);
  const accordionButtonRenderer = useCallback(
    (props: IAccordionElementButton) => {
      const Component = row.AccordionButtonRenderer ?? ButtonComponent;

      return (
        <Component {...props} row={row} columns={columns} rowIndex={rowIndex} />
      );
    },
    [ButtonComponent, columns, row, rowIndex],
  );

  if (!Array.isArray(indexColumns) || !columns || !indexColumns[0]?.column)
    return null;

  const hasBold =
    (row.className?.indexOf('bold') ?? -1) !== -1 ||
    row.cells.find(
      (current) => (current.className?.indexOf('bold') ?? -1) !== -1,
    );
  const AccordionElementRenderer = row?.AccordionElementRenderer;

  return (
    <>
      <PrimitiveAccordionElement
        ButtonComponent={(p) =>
          accordionButtonRenderer({
            ...p,
            expandedIcons: hideColumns ? null : p.expandedIcons,
            onClick: hideColumns ? () => {} : p.onClick,
            children: titleChildren,
          })
        }
        className={`responsiveTable__accordionElement ${row.className ?? ''} ${
          hasBold ? 'global' : ''
        }`}
        contentClassName="responsiveTable__accordionElement__content"
        id={row.id}
        title={title}
        fixStyleOverflowVisible
      >
        {!AccordionElementRenderer ? (
          columns.map((currentColumn, columnIndex) => {
            if (
              currentColumn.label?.trim() === '' &&
              currentColumn.showInAccordionTitle
            )
              return null;
            const Renderer =
              row.cells[columnIndex].AccordionRenderer ??
              currentColumn.AccordionRenderer ??
              AccordionCell;

            return currentColumn.hideFromAccordion ||
              currentColumn.showAsAdditional ? null : (
              <Renderer
                cell={row.cells[columnIndex]}
                column={currentColumn}
                columnIndex={columnIndex}
                key={currentColumn.name}
                row={row}
                rowIndex={rowIndex}
              />
            );
          })
        ) : (
          <AccordionElementRenderer row={row} rowIndex={rowIndex} />
        )}

        {columns
          .filter((current) => !!current.showAsAdditional)
          .map((currentColumn, columnIndex) => {
            const Renderer =
              row.cells[columnIndex].AccordionRenderer ??
              currentColumn.AccordionRenderer ??
              AccordionCell;

            return currentColumn.hideFromAccordion ? null : (
              <Renderer
                cell={row.cells[columnIndex]}
                column={currentColumn}
                columnIndex={columnIndex}
                key={currentColumn.name}
                row={row}
                rowIndex={rowIndex}
              />
            );
          })}
      </PrimitiveAccordionElement>
    </>
  );
};

export default AccordionElement;
